import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CalendarClock, ChevronDown, ChevronRight, Pencil, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { ExampleDataNote, Field, HistoryList, MiniStat, OperationalPill, RefSelect, RefsInlineStatus, SelectField, ServiceStatusPill, inputClass, useDriftRefs } from '@/components/app/DriftUI';
import { AttentionList, CaseList, CaseSheetHost, CaseViewChips, CASE_VIEWS, MachineStatusCard, MeldFeilSheet, ServiceSheet, useCaseParam } from '@/components/app/Maskinsaker';
import { getMachine, hoursText, listEvents, listMachines, patchMachine, serviceLine } from '@/lib/driftApi';
import { MACHINE_TYPE_LABELS } from '@/lib/labels';
import { fmtNumber } from '@/domain/numbers';
import { formatDateShort } from '@/domain/time';
import { cn } from '@/lib/utils';
/*
  Maskinpark, drift og service – utvider eksisterende Maskiner (samme maskin-ID-er som produksjonsdataene).
  Register (internnr, type/årsmodell, lokasjon, timeteller/km, ansvarlig), driftsstatus (kan ikke brukes / avventer
  frigivelse / i drift), servicestatus fra en eksempelplan («Eksempelplan» – ikke kundens serviceintervaller), og
  saker: feilmeldinger, service og vedlikehold med saksgang og historikk (se Maskinsaker.jsx).
  Timeteller/km er registerverdier (livstid) – ikke motortimer fra telematikk og ikke registrert maskintid fra timeføring.
*/

const meterText = (m) => (m.meter === null || m.meter === undefined ? '–' : `${fmtNumber(m.meter, 0)} ${m.meterType === 'km' ? 'km' : 't'}`);

/* ---------- Fane på /maskiner: Maskinpark og service ---------- */

export function MaskinparkPanel() {
  const navigate = useNavigate();
  const { openCase } = useCaseParam();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('loading');
  const [fault, setFault] = useState({ open: false, machineId: '' });
  const [service, setService] = useState({ open: false, machineId: '' });
  const [view, setView] = useState('apne');

  const load = useCallback(async () => {
    try {
      const [m, ev] = await Promise.all([listMachines(), listEvents()]);
      setData(m);
      setEvents(ev.events);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const machines = data?.machines ?? [];
  const summary = data?.summary;
  const shown = useMemo(() => events.filter(CASE_VIEWS.find((v) => v.id === view)?.match ?? (() => true)), [events, view]);
  const serviceMeta = summary ? [summary.forfalt ? `${summary.forfalt} forfalt` : null, summary.snart ? `${summary.snart} snart` : null, summary.ukjent ? `${summary.ukjent} ukjent` : null].filter(Boolean).join(' · ') || `${summary.ok} OK` : '';

  return (
    <div className="space-y-5" data-testid="maskinpark-panel">
      <ExampleDataNote>Serviceplaner er eksempler («Eksempelplan»), ikke kundens serviceintervaller. Timeteller/km er registerverdier, ikke motortimer fra telematikk. Ingen melding sendes når feil meldes.</ExampleDataNote>

      <Panel>
        <div className="grid grid-cols-2 divide-x divide-[var(--hr-hairline)] lg:grid-cols-4" data-testid="maskinpark-stats">
          <MiniStat label="Kan ikke brukes" value={summary ? fmtNumber(summary.blocked, 0) : '–'} meta={summary?.blocked ? 'Åpen feil som stopper maskinen' : 'Ingen maskiner stoppet'} tone={summary?.blocked ? 'amber' : undefined} testId="maskinpark-stat-blocked" />
          <MiniStat label="Avventer frigivelse" value={summary ? fmtNumber(summary.awaitingClearance, 0) : '–'} meta="Løst, men ikke frigitt til bruk" tone={summary?.awaitingClearance ? 'amber' : undefined} testId="maskinpark-stat-awaiting" />
          <MiniStat label="Åpne saker" value={summary ? fmtNumber(summary.openEvents, 0) : '–'} meta={summary ? `${summary.openFaults} feil · ${summary.openService} service${summary.overdue ? ` · ${summary.overdue} forfalt frist` : ''}` : ''} tone={summary?.overdue ? 'amber' : undefined} testId="maskinpark-stat-open" />
          <MiniStat label="Service" value={summary ? `${summary.forfalt + summary.snart} å følge opp` : '–'} meta={serviceMeta} tone={summary?.forfalt || summary?.ukjent ? 'amber' : undefined} testId="maskinpark-stat-service" />
        </div>
      </Panel>

      <Panel data-testid="attention-panel">
        <PanelHeader
          title="Hva trenger oppfølging?"
          description="Maskiner som ikke kan brukes, avventer frigivelse, har forfalte frister eller service å følge opp."
          actions={
            <>
              <Button variant="outline" className="h-10 rounded-[10px] bg-white text-[14px]" onClick={() => setService({ open: true, machineId: '' })} data-testid="maskinpark-new-service-button">
                <CalendarClock className="mr-1.5 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Planlegg service
              </Button>
              <Button className="h-10 rounded-[10px] bg-[var(--hr-heading)] text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" onClick={() => setFault({ open: true, machineId: '' })} data-testid="maskinpark-new-fault-button">
                <AlertTriangle className="mr-1.5 h-4 w-4" aria-hidden="true" /> Meld feil
              </Button>
            </>
          }
        />
        {status === 'loading' && <div className="p-5"><SkeletonTable rows={3} /></div>}
        {status === 'error' && <div className="p-5"><Note tone="amber">Kunne ikke hente maskinregisteret. <Button variant="link" className="h-auto px-1" onClick={load}>Prøv igjen</Button></Note></div>}
        {status === 'ready' && <AttentionList machines={machines} events={events} onOpenCase={openCase} />}
      </Panel>

      <Panel>
        <PanelHeader title="Maskinregister" description="Driftsstatus, timeteller/km, service og åpne saker. Klikk en maskin for maskinkort og historikk." />
        {status === 'loading' && <div className="p-5"><SkeletonTable rows={8} /></div>}
        {status === 'ready' && (
          <>
            {/* Mobil: kort med det viktigste først – driftsstatus, service og åpne saker. */}
            <ul className="divide-y divide-[var(--hr-hairline)] md:hidden" data-testid="maskinpark-cards">
              {machines.map((m) => (
                <li key={m.machineId}>
                  <button type="button" onClick={() => navigate(`/maskiner/${m.machineId}`)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={`maskinpark-card-${m.machineId}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="hr-num rounded-[6px] border border-[var(--hr-hairline)] bg-white px-1.5 py-0.5 text-[13px] font-semibold text-[var(--hr-heading)]">{m.internalNo}</span>
                        <span className="text-[15px] font-semibold text-[var(--hr-heading)]">{m.name}</span>
                        <OperationalPill status={m.operational.status} label={m.operational.label} />
                      </div>
                      <div className="mt-0.5 text-[13px] text-[var(--hr-muted)]">{MACHINE_TYPE_LABELS[m.type] ?? m.type} · {m.modelYear} · {m.location}</div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <ServiceStatusPill status={m.service.status} />
                        <span className={cn('text-[13px]', m.service.status === 'ukjent' ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted)]')}>{serviceLine(m.service, { short: true })}</span>
                      </div>
                      <div className="hr-tabular mt-1 text-[13px] text-[var(--hr-muted)]">
                        {meterText(m)} <span className="text-[var(--hr-muted-2)]">(register)</span> · {m.cases.open ? `${m.cases.open} ${m.cases.open === 1 ? 'åpen sak' : 'åpne saker'}` : 'ingen åpne saker'}
                        {m.cases.overdue ? <span className="text-[var(--hr-amber)]"> · {m.cases.overdue} forfalt</span> : null}
                      </div>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto md:block">
            <Table data-testid="maskinpark-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Maskin</TableHead>
                  <TableHead>Driftsstatus</TableHead>
                  <TableHead className="hidden md:table-cell">Lokasjon</TableHead>
                  <TableHead className="text-right">Timeteller / km</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead className="hidden whitespace-nowrap text-right lg:table-cell">Åpne saker</TableHead>
                  <TableHead className="hidden xl:table-cell">Ansvarlig</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {machines.map((m) => (
                  <TableRow key={m.machineId} className="cursor-pointer" onClick={() => navigate(`/maskiner/${m.machineId}`)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && navigate(`/maskiner/${m.machineId}`)} data-testid={`maskinpark-row-${m.machineId}`}>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <span className="hr-num rounded-[6px] border border-[var(--hr-hairline)] bg-white px-1.5 py-0.5 text-[13px] font-semibold text-[var(--hr-heading)]">{m.internalNo}</span>
                        <span className="font-medium text-[var(--hr-heading)]">{m.name}</span>
                      </span>
                      <div className="mt-0.5 text-[13px] text-[var(--hr-muted)]">{MACHINE_TYPE_LABELS[m.type] ?? m.type} · {m.modelYear}</div>
                    </TableCell>
                    <TableCell><OperationalPill status={m.operational.status} label={m.operational.label} data-testid={`maskinpark-operational-${m.machineId}`} /></TableCell>
                    <TableCell className="hidden md:table-cell">{m.location}</TableCell>
                    <TableCell className="hr-tabular whitespace-nowrap text-right">{meterText(m)}</TableCell>
                    <TableCell>
                      <ServiceStatusPill status={m.service.status} data-testid={`maskinpark-service-${m.machineId}`} />
                      <div className={cn('mt-0.5 hidden whitespace-nowrap text-[13px] xl:block', m.service.status === 'ukjent' ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{serviceLine(m.service)}</div>
                    </TableCell>
                    <TableCell className={cn('hr-tabular hidden text-right lg:table-cell', m.cases.overdue && 'text-[var(--hr-amber)]')}>{m.cases.open || <span className="text-[var(--hr-muted-2)]">0</span>}{m.cases.overdue ? <span className="ml-1 text-[12px]">({m.cases.overdue} forfalt)</span> : null}</TableCell>
                    <TableCell className="hidden xl:table-cell">{m.responsible?.name ?? '–'}</TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-[var(--hr-muted-2)]" aria-hidden="true" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </>
        )}
      </Panel>

      <Panel data-testid="cases-panel">
        <PanelHeader
          title="Saker og historikk"
          description="Feilmeldinger, service og vedlikehold på alle maskiner. Klikk en sak for saksgang, notater og vedlegg."
          actions={
            <Button asChild variant="outline" size="sm" className="h-9 rounded-[10px] bg-white">
              <Link to="/rapporter/hendelser" data-testid="cases-report-link">
                Rapport og eksport <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </Button>
          }
        />
        <div className="border-b border-[var(--hr-hairline)] px-4 py-3 sm:px-5">
          <CaseViewChips value={view} onChange={setView} events={events} testId="cases-view" />
        </div>
        {status === 'ready' && <CaseList events={shown} onOpen={openCase} emptyText={view === 'apne' ? 'Ingen åpne saker.' : 'Ingen saker i dette utvalget.'} testId="cases" />}
      </Panel>

      <MeldFeilSheet open={fault.open} onOpenChange={(o) => setFault((d) => ({ ...d, open: o }))} machines={machines} machineId={fault.machineId} onSaved={load} />
      <ServiceSheet open={service.open} onOpenChange={(o) => setService((d) => ({ ...d, open: o }))} machines={machines} machineId={service.machineId} onSaved={load} />
      <CaseSheetHost onChanged={load} />
    </div>
  );
}

/* ---------- Oversikt: «Maskiner som trenger oppfølging» ---------- */

/** Kompakt panel for Oversikt. Henter maskinregisteret og åpne saker; lenker til Maskiner og åpner saker i ark. */
export function MachineAttentionPanel({ limit = 5 }) {
  const { openCase } = useCaseParam();
  const [machines, setMachines] = useState(null);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('loading');
  const load = useCallback(async () => {
    try {
      const [m, ev] = await Promise.all([listMachines(), listEvents({ open: 1 })]);
      setMachines(m.machines);
      setEvents(ev.events);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const count = machines ? machines.filter((m) => m.needsFollowUp).length : 0;
  const blocked = machines ? machines.filter((m) => m.operational?.status === 'stopp').length : 0;
  return (
    <Panel data-testid="overview-attention-panel">
      <PanelHeader
        title="Maskiner som trenger oppfølging"
        description={status === 'ready' ? `${count} av ${machines.length} maskiner${blocked ? ` · ${blocked} kan ikke brukes` : ''} · feil, frigivelse, frister og service (eksempeldata)` : 'Feil, frigivelse, frister og service (eksempeldata).'}
        actions={
          <Button asChild variant="outline" size="sm" className="h-9 rounded-[10px] bg-white">
            <Link to="/maskiner?fane=maskinpark" data-testid="overview-attention-all-link">
              Maskinpark og service <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        }
      />
      {status === 'loading' && <div className="p-5"><SkeletonTable rows={3} /></div>}
      {status === 'error' && <div className="p-5"><Note tone="amber">Kunne ikke hente maskinregisteret. <Button variant="link" className="h-auto px-1" onClick={load}>Prøv igjen</Button></Note></div>}
      {status === 'ready' && <AttentionList machines={machines} events={events} onOpenCase={openCase} limit={limit} compact testId="overview-attention" />}
      <CaseSheetHost onChanged={load} />
    </Panel>
  );
}

/* ---------- Paneler på /maskiner/:id ---------- */

function EditMachineDialog({ open, onOpenChange, machine, onSaved }) {
  const { refs, error: refsError, loading: refsLoading, retry: retryRefs } = useDriftRefs();
  const [form, setForm] = useState({ location: '', meter: '', responsibleId: '', serviceKind: 'h', serviceInterval: '', serviceLastAt: '', serviceLastDate: '', reason: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && machine) {
      const p = machine.servicePlan ?? {};
      setForm({ location: machine.location ?? '', meter: machine.meter ?? '', responsibleId: machine.responsibleId ?? '', serviceKind: p.kind ?? machine.meterType ?? 'h', serviceInterval: p.intervalValue ?? '', serviceLastAt: p.lastAt ?? '', serviceLastDate: p.lastDate ?? '', reason: '' });
    }
  }, [open, machine]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const p = machine.servicePlan ?? {};
      const body = { reason: form.reason.trim() || undefined };
      if (form.location !== (machine.location ?? '')) body.location = form.location;
      if (form.meter !== '' && Number(form.meter) !== machine.meter) body.meter = Number(form.meter);
      if (form.responsibleId && form.responsibleId !== machine.responsibleId) body.responsibleId = form.responsibleId;
      if (form.serviceKind !== p.kind) body.serviceKind = form.serviceKind;
      if (form.serviceInterval !== '' && Number(form.serviceInterval) !== p.intervalValue) body.serviceInterval = Number(form.serviceInterval);
      if (form.serviceLastAt !== '' && Number(form.serviceLastAt) !== p.lastAt) body.serviceLastAt = Number(form.serviceLastAt);
      if ((form.serviceLastDate || '') !== (p.lastDate || '')) body.serviceLastDate = form.serviceLastDate || '';
      await patchMachine(machine.machineId, body);
      toast.success('Maskinkort oppdatert', { description: 'Endringene ligger i maskinens historikk.' });
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error('Kunne ikke lagre', { description: err.message });
    } finally {
      setSaving(false);
    }
  };
  if (!machine) return null;
  const unit = form.serviceKind === 'km' ? 'km' : form.serviceKind === 'dato' ? 'dager' : 't';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] max-w-[480px] overflow-y-auto rounded-[16px]" data-testid="edit-machine-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-[16px]">Rediger maskinkort</DialogTitle>
          <DialogDescription className="text-[13px]">Plassering, teller, ansvarlig og eksempelplan for service. Alle endringer lagres med før/etter.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Plassering" htmlFor="em-loc">
            <Input id="em-loc" value={form.location} onChange={(e) => set('location')(e.target.value)} maxLength={80} className={inputClass} data-testid="edit-machine-location" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={machine.meterType === 'km' ? 'Km-stand (register)' : 'Timeteller (register)'} htmlFor="em-meter" hint="Livstidsverdi – ikke registrert maskintid">
              <Input id="em-meter" type="number" inputMode="numeric" min={0} value={form.meter} onChange={(e) => set('meter')(e.target.value)} className={inputClass} data-testid="edit-machine-meter" />
            </Field>
            <Field label="Ansvarlig" htmlFor="em-resp">
              <RefSelect id="em-resp" value={form.responsibleId} onValueChange={set('responsibleId')} list={refs?.employees} getHint={(x) => x.role} testId="edit-machine-responsible" />
              <RefsInlineStatus refs={refs} error={refsError} loading={refsLoading} onRetry={retryRefs} testId="edit-machine-refs" />
            </Field>
          </div>
          <section className="rounded-[12px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] p-3.5">
            <h3 className="text-[14px] font-semibold text-[var(--hr-heading)]">Serviceplan (Eksempelplan)</h3>
            <p className="mt-0.5 text-[13px] leading-5 text-[var(--hr-muted)]">Etter timer, km eller dato. Mangler siste service, vises status som «Ukjent» – aldri som OK.</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Grunnlag" htmlFor="em-kind">
                <SelectField id="em-kind" value={form.serviceKind} onValueChange={set('serviceKind')} options={[{ value: 'h', label: 'Timer' }, { value: 'km', label: 'Km' }, { value: 'dato', label: 'Dato (dager)' }]} testId="edit-machine-service-kind" />
              </Field>
              <Field label={`Intervall (${unit})`} htmlFor="em-interval">
                <Input id="em-interval" type="number" inputMode="numeric" min={1} value={form.serviceInterval} onChange={(e) => set('serviceInterval')(e.target.value)} className={inputClass} data-testid="edit-machine-service-interval" />
              </Field>
              {form.serviceKind !== 'dato' && (
                <Field label={`Sist service (${unit})`} htmlFor="em-last" hint="Tomt = ukjent">
                  <Input id="em-last" type="number" inputMode="numeric" min={0} value={form.serviceLastAt} onChange={(e) => set('serviceLastAt')(e.target.value)} className={inputClass} data-testid="edit-machine-service-last" />
                </Field>
              )}
              <Field label="Sist service (dato)" htmlFor="em-lastdate" hint={form.serviceKind === 'dato' ? 'Tomt = ukjent' : 'Valgfritt'}>
                <Input id="em-lastdate" type="date" value={form.serviceLastDate} onChange={(e) => set('serviceLastDate')(e.target.value)} className={inputClass} data-testid="edit-machine-service-lastdate" />
              </Field>
            </div>
          </section>
          <Field label="Årsak til endringen" htmlFor="em-reason" hint="Valgfritt – vises i historikken">
            <Input id="em-reason" value={form.reason} onChange={(e) => set('reason')(e.target.value)} maxLength={300} className={inputClass} data-testid="edit-machine-reason" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-[10px]" onClick={() => onOpenChange(false)} disabled={saving}>Avbryt</Button>
          <Button className="rounded-[10px] bg-[var(--hr-heading)] text-white hover:bg-[var(--hr-navy)]" onClick={save} disabled={saving} data-testid="edit-machine-save">{saving ? 'Lagrer …' : 'Lagre'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Maskinkort på /maskiner/:id: status, saker, historikk og timeføringer fra registeret (eksempeldata).
 * `from`/`to` er valgt periode for registrert maskintid (fra timeføring) – registerets teller er livstid.
 * `middle` gjengis mellom statuskortet og sakspanelene (f.eks. faner + telematikk); `showCases=false` skjuler panelene.
 */
export function MachineRegisterPanels({ machineId, from, to, periodLabel, middle = null, showCases = true }) {
  const { openCase } = useCaseParam();
  const [m, setM] = useState(null);
  const [status, setStatus] = useState('loading');
  const [fault, setFault] = useState(false);
  const [service, setService] = useState(false);
  const [edit, setEdit] = useState(false);
  const [view, setView] = useState('alle');
  const [showChanges, setShowChanges] = useState(false);
  const load = useCallback(async () => {
    try {
      setM(await getMachine(machineId, { from, to }));
      setStatus('ready');
    } catch (e) {
      setStatus(e?.status === 404 ? 'missing' : 'error');
    }
  }, [machineId, from, to]);
  useEffect(() => {
    load();
  }, [load]);

  const history = useMemo(() => (m ? m.events.filter(CASE_VIEWS.find((v) => v.id === view)?.match ?? (() => true)) : []), [m, view]);

  if (status === 'loading') return <><Panel><div className="p-5"><SkeletonTable rows={3} /></div></Panel>{middle}</>;
  if (status === 'missing') return <><Panel><div className="p-5"><Note>Maskinen finnes ikke i maskinregisteret (eksempeldata).</Note></div></Panel>{middle}</>;
  if (status === 'error' || !m) return <><Panel><div className="p-5"><Note tone="amber">Kunne ikke hente maskinkortet. <Button variant="link" className="h-auto px-1" onClick={load}>Prøv igjen</Button></Note></div></Panel>{middle}</>;

  const open = m.openEvents ?? [];
  const awaiting = m.events.filter((e) => e.awaitingClearance);
  const mv = { ...m, typeLabel: MACHINE_TYPE_LABELS[m.type] ?? m.type };

  return (
    <>
      <MachineStatusCard
        m={mv}
        periodLabel={periodLabel}
        actions={
          <>
            <Button variant="outline" size="sm" className="h-9 rounded-[10px] bg-white" onClick={() => setEdit(true)} data-testid="machine-edit-button">
              <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Rediger
            </Button>
            <Button variant="outline" size="sm" className="h-9 rounded-[10px] bg-white" onClick={() => setService(true)} data-testid="machine-new-service-button">
              <Wrench className="mr-1.5 h-3.5 w-3.5 text-[var(--hr-muted)]" aria-hidden="true" /> Planlegg service
            </Button>
            <Button size="sm" className="h-9 rounded-[10px] bg-[var(--hr-heading)] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" onClick={() => setFault(true)} data-testid="machine-new-fault-button">
              <AlertTriangle className="mr-1.5 h-4 w-4" aria-hidden="true" /> Meld feil
            </Button>
          </>
        }
      />

      {middle}

      {showCases && (
      <>
      <Panel data-testid="machine-open-cases-panel">
        <PanelHeader title="Åpne saker" description={open.length ? `${open.filter((e) => e.type === 'feilmelding').length} feil · ${open.filter((e) => e.type !== 'feilmelding').length} service/vedlikehold. Klikk en sak for å følge opp.` : 'Ingen åpne saker på maskinen.'} />
        {awaiting.map((ev) => (
          <div key={ev.id} className="flex flex-col gap-2 border-b border-[rgba(154,91,0,0.25)] bg-[var(--hr-amber-bg)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5" data-testid={`machine-awaiting-${ev.id}`}>
            <p className="text-[14px] leading-5 text-[var(--hr-text)]">
              <span className="font-semibold text-[var(--hr-amber)]">Avventer frigivelse:</span> «{ev.title}» er løst{ev.resolution?.date ? ` ${formatDateShort(ev.resolution.date)}` : ''}, men maskinen er ikke frigitt til bruk.
            </p>
            <Button size="sm" className="h-9 shrink-0 rounded-[10px] bg-[var(--hr-heading)] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" onClick={() => openCase(ev.id)} data-testid={`machine-awaiting-open-${ev.id}`}>
              Åpne sak for frigivelse
            </Button>
          </div>
        ))}
        <CaseList events={open} onOpen={openCase} showMachine={false} emptyText="Ingen åpne saker – meld feil eller planlegg service over." testId="machine-open-cases" />
      </Panel>

      <Panel data-testid="machine-history-panel">
        <PanelHeader title="Historikk" description={`${m.events.length} saker på maskinen – feilmeldinger, service og vedlikehold i kronologisk rekkefølge.`} />
        <div className="border-b border-[var(--hr-hairline)] px-4 py-3 sm:px-5">
          <CaseViewChips value={view} onChange={setView} events={m.events} testId="machine-history-view" />
        </div>
        <CaseList events={history} onOpen={openCase} showMachine={false} emptyText="Ingen saker i dette utvalget." testId="machine-history" />
        <Collapsible open={showChanges} onOpenChange={setShowChanges}>
          <div className="flex items-center justify-between gap-2 border-t border-[var(--hr-hairline)] px-4 py-2.5 sm:px-5">
            <p className="text-[13px] text-[var(--hr-muted)]">Endringer i maskinkortet (teller, plassering, ansvarlig, serviceplan): {m.history?.length ?? 0}</p>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px]" data-testid="machine-changes-toggle">
                <ChevronDown className={cn('mr-1 h-3.5 w-3.5 transition-transform duration-150', showChanges && 'rotate-180')} aria-hidden="true" /> {showChanges ? 'Skjul' : 'Vis'}
              </Button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <div className="px-4 pb-4 sm:px-5">
              <HistoryList history={m.history} testId="machine-history-changes" />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Panel>

      <Panel data-testid="machine-time-panel">
        <PanelHeader
          title="Registrert maskintid"
          description={`${m.timeSums.count} føringer · ${hoursText(m.timeSums.machineHours)} maskintimer i ${periodLabel ?? 'perioden'} (fra timeføring – ikke registerets teller)`}
          actions={
            <Button asChild variant="outline" size="sm" className="h-9 rounded-[10px] bg-white">
              <Link to={`/timer?machineId=${m.machineId}`} data-testid="machine-time-link">Åpne i Timer</Link>
            </Button>
          }
        />
        {m.timeEntries.length === 0 ? (
          <p className="px-5 py-6 text-[14px] text-[var(--hr-muted)]">Ingen timeføringer på maskinen i perioden.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dato</TableHead>
                  <TableHead>Medarbeider</TableHead>
                  <TableHead className="hidden md:table-cell">Prosjekt</TableHead>
                  <TableHead className="hidden lg:table-cell">Aktivitet</TableHead>
                  <TableHead className="text-right">Maskintimer</TableHead>
                  <TableHead className="hidden text-right sm:table-cell">Arbeidstimer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {m.timeEntries.slice(0, 12).map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="hr-tabular whitespace-nowrap">{formatDateShort(e.date)}</TableCell>
                    <TableCell className="font-medium text-[var(--hr-heading)]">{e.employee?.name}</TableCell>
                    <TableCell className="hidden md:table-cell">{e.project?.name}{e.subproject ? <span className="text-[var(--hr-muted)]"> · {e.subproject.name}</span> : null}</TableCell>
                    <TableCell className="hidden lg:table-cell">{e.activity?.label}</TableCell>
                    <TableCell className="hr-tabular text-right font-semibold text-[var(--hr-heading)]">{hoursText(e.machineTotalHours)}</TableCell>
                    <TableCell className="hr-tabular hidden text-right text-[var(--hr-muted)] sm:table-cell">{hoursText(e.hours)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {m.timeEntries.length > 12 && <p className="px-5 py-2 text-[13px] text-[var(--hr-muted)]">Viser de 12 siste. Se alle i Timer.</p>}
          </div>
        )}
      </Panel>
      </>
      )}

      <MeldFeilSheet open={fault} onOpenChange={setFault} machines={[m]} machineId={m.machineId} onSaved={load} />
      <ServiceSheet open={service} onOpenChange={setService} machines={[m]} machineId={m.machineId} onSaved={load} />
      <EditMachineDialog open={edit} onOpenChange={setEdit} machine={m} onSaved={load} />
      <CaseSheetHost onChanged={load} machine={m} />
    </>
  );
}
